import { motion } from 'framer-motion';
import { MotionImage, useSanitizedTranslation, StackedContent, XumoButton } from 'ui';

export interface ImgColumn {
  title?: string;
  body: string;
  image: string;
}

export interface ImageColumnsProps {
  title?: any;
  columns: ImgColumn[];
  type?: 1 | 2;
  cta?: any | any[];
}

export const ImageColumns = ({ columns, title, type = 1, cta }: ImageColumnsProps) => {
  const { t } = useSanitizedTranslation();

  const ctas = Array.isArray(cta) ? cta : cta ? [cta] : [];

  if (type === 2) {
    return (
      <motion.section className="relative z-40 flex w-full justify-center bg-white" data-testid={'text-section'}>
        <div className="wrapper">
          <div className="content-wrapper-x flex flex-col items-center justify-center py-16 lg:py-28">
            <StackedContent center noMargin minHeight content={title} />
            <div className="mt-16 grid w-full grid-cols-1 items-stretch gap-10 md:grid-cols-3 md:gap-0 lg:mt-20">
              {columns.map((column, index) => (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20% 0% -20% 0%' }}
                  transition={{ ease: [0, 1, 1, 1], duration: 1.0, delay: 0.5 + index * 0.15 }}
                  key={column.image || column.title}
                  className="relative flex flex-col items-center px-6 text-center"
                >
                  {/* HR  */}
                  {index > 0 && <div className="absolute bottom-0 left-0 top-0 hidden w-px bg-black md:block" />}
                  <div className="relative mb-12 flex h-[96px] w-[96px] items-center justify-center overflow-hidden rounded-full lg:mb-16">
                    <MotionImage src={column.image} alt="" priority fill sizes="96px" className="h-full w-full object-contain" />
                  </div>
                  <div className={`type-body-uniform flex grow flex-col items-center justify-start text-center`}>
                    <p data-i18n={column.body} dangerouslySetInnerHTML={{ __html: t(column.body) }} />
                  </div>
                </motion.div>
              ))}
            </div>
            {ctas.length > 0 && (
              <div className="mt-16 flex flex-col items-center justify-center gap-4 md:flex-row lg:mt-20">
                {ctas.map((button, index) => (
                  <XumoButton key={button.href || button.label || index} {...button} />
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section className="relative z-40 flex w-full justify-center bg-white lg:pt-24" data-testid={'text-section'}>
      <div className="wrapper">
        <div className={`content-wrapper-x flex flex-col items-center justify-center py-8 lg:py-20`}>
          <StackedContent center noMargin minHeight content={title} />
          <div className="relative grid w-full grid-cols-1 flex-wrap gap-8 md:grid-cols-3">
            {columns.map((column, index) => (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-20% 0% -20% 0%' }}
                transition={{ ease: [0, 1, 1, 1], duration: 1.0, delay: 0.5 + index * 0.15 }}
                key={column.image}
                className="relative space-y-4 overflow-clip"
              >
                <motion.div key={column.image} className="relative mb-8 aspect-square w-full overflow-clip rounded-xl">
                  <MotionImage src={column.image} alt="" priority fill sizes="100vw" className="h-full w-full object-cover" />
                </motion.div>
                <div className="flex flex-col items-center justify-center gap-3">
                  <p data-i18n={column.title} className="type-body flex grow text-center max-lg:my-4" dangerouslySetInnerHTML={{ __html: t(column.title) }}></p>
                  <p data-i18n={column.body} className="flex grow text-center max-lg:mb-4" dangerouslySetInnerHTML={{ __html: t(column.body) }}></p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
};
